import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { useAllActivites } from '@/hooks/queries/useActivites';
import { useTiers } from '@/hooks/queries/useTiers';
import { useCreatePlanPaiement } from '@/hooks/queries/usePlansPaiement';
import { formatDA } from '@/lib/format';
import { t } from '@/locales/i18n';
import type { EcheanceSens, PlanPaiementFrequence } from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entrepriseId: string;
  onSaved: () => void;
}

export function PlanPaiementFormDialog({
  open,
  onOpenChange,
  entrepriseId,
  onSaved,
}: Props): JSX.Element {
  const { data: tiers = [] } = useTiers({ entrepriseId, search: '' });
  const { data: activites = [] } = useAllActivites();
  const create = useCreatePlanPaiement();

  const today = new Date().toISOString().slice(0, 10);

  const [sens, setSens] = useState<EcheanceSens>('a_encaisser');
  const [tierId, setTierId] = useState('');
  const [activiteId, setActiviteId] = useState('');
  const [factureRef, setFactureRef] = useState('');
  const [motif, setMotif] = useState('');
  const [montant, setMontant] = useState('0');
  const [nbTranches, setNbTranches] = useState('3');
  const [datePremiere, setDatePremiere] = useState(today);
  const [frequence, setFrequence] = useState<PlanPaiementFrequence>('mensuelle');
  const [intervalle, setIntervalle] = useState('30');
  const [observation, setObservation] = useState('');
  const [error, setError] = useState<string | null>(null);

  const tiersFiltres = useMemo(
    () =>
      tiers.filter((tt) =>
        sens === 'a_encaisser'
          ? tt.type === 'client_b2b' || tt.type === 'client_b2c'
          : tt.type === 'fournisseur' || tt.type === 'sous_traitant',
      ),
    [tiers, sens],
  );
  const activitesEnt = useMemo(
    () => activites.filter((a) => a.entreprise_id === entrepriseId),
    [activites, entrepriseId],
  );

  useEffect(() => {
    if (!open) return;
    setSens('a_encaisser');
    setTierId('');
    setActiviteId('');
    setFactureRef('');
    setMotif('');
    setMontant('0');
    setNbTranches('3');
    setDatePremiere(today);
    setFrequence('mensuelle');
    setIntervalle('30');
    setObservation('');
    setError(null);
  }, [open, today]);

  const m = Number(montant);
  const n = Number(nbTranches);
  const tranchePreview = n > 0 && m > 0 ? Math.round((m / n) * 100) / 100 : 0;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!tierId) return setError('Tiers obligatoire');
    if (!(m > 0)) return setError('Montant invalide');
    if (n < 2 || n > 60) return setError('Nombre de tranches entre 2 et 60');

    try {
      await create.mutateAsync({
        entreprise_id: entrepriseId,
        tier_id: tierId,
        sens,
        activite_id: activiteId || null,
        facture_ref: factureRef || null,
        motif: motif || null,
        montant_total: m,
        nb_echeances: n,
        date_premiere: datePremiere,
        frequence,
        intervalle_jours: frequence === 'sur_mesure' ? Number(intervalle) : null,
        observation: observation || null,
      });
      toast.success(`Plan créé — ${n} tranches générées`);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('echeances.plans.newPlan')}
      onSubmit={handleSubmit}
      isSubmitting={create.isPending}
      size="lg"
    >
      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormSelect
          label="Sens"
          value={sens}
          onChange={(v) => setSens(v as EcheanceSens)}
          options={[
            { value: 'a_encaisser', label: 'À encaisser (client paye)' },
            { value: 'a_payer', label: 'À payer (fournisseur)' },
          ]}
          required
        />
        <FormSelect
          label="Tiers"
          value={tierId}
          onChange={setTierId}
          required
          options={[
            { value: '', label: '— Sélectionner —' },
            ...tiersFiltres.map((tt) => ({
              value: tt.id,
              label: `${tt.code ? tt.code + ' · ' : ''}${tt.raison_sociale}`,
            })),
          ]}
        />
        <FormSelect
          label="Activité"
          value={activiteId}
          onChange={setActiviteId}
          options={[
            { value: '', label: '— Aucune —' },
            ...activitesEnt.map((a) => ({ value: a.id, label: a.nom })),
          ]}
        />
        <FormField
          label="Référence facture"
          value={factureRef}
          onChange={(e) => setFactureRef(e.target.value)}
        />
        <FormField
          label="Montant total"
          type="number"
          step="0.01"
          min="0.01"
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
          required
          hint={tranchePreview > 0 ? `≈ ${formatDA(tranchePreview)} / tranche` : undefined}
        />
        <FormField
          label="Nombre de tranches"
          type="number"
          step="1"
          min="2"
          max="60"
          value={nbTranches}
          onChange={(e) => setNbTranches(e.target.value)}
          required
        />
        <FormField
          label="Date 1ère tranche"
          type="date"
          value={datePremiere}
          onChange={(e) => setDatePremiere(e.target.value)}
          required
        />
        <FormSelect
          label="Fréquence"
          value={frequence}
          onChange={(v) => setFrequence(v as PlanPaiementFrequence)}
          options={(
            [
              'hebdomadaire',
              'bimensuelle',
              'mensuelle',
              'trimestrielle',
              'sur_mesure',
            ] as PlanPaiementFrequence[]
          ).map((f) => ({ value: f, label: t(`echeances.plans.frequence.${f}`) }))}
        />
        {frequence === 'sur_mesure' && (
          <FormField
            label="Intervalle (jours)"
            type="number"
            step="1"
            min="1"
            value={intervalle}
            onChange={(e) => setIntervalle(e.target.value)}
            required
          />
        )}
        <FormField
          label="Motif"
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          fullWidth
        />
        <FormTextarea
          label="Observation"
          value={observation}
          onChange={(e) => setObservation(e.target.value)}
          fullWidth
          rows={2}
        />
      </div>
    </FormDialog>
  );
}
